/**
 * The rated exchange for one feedback item, rendered through the design
 * system's Chat.Transcript so a replay looks like the conversation it came
 * from. Lazy: nothing is fetched until the section is opened. The full
 * thread lives on its own page.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Chat, Skeleton } from '@servicecompressionllc/sc-design-system';
import type { ReviewTranscriptMessage } from '../../lib/types.js';
import { useReviewTranscript } from './useFeedbackLogic.js';

const retrievalLine = (message: ReviewTranscriptMessage | undefined) =>
  message?.retrieval && message.retrieval.length > 0 ? (
    <p className="mt-1 text-xs text-ink-muted">
      Retrieved: {message.retrieval.map((r) => r.subjectName ?? r.title ?? 'unknown').join(', ')}
    </p>
  ) : null;

export const TranscriptView = ({
  conversationId,
  messageId,
}: {
  conversationId: string;
  messageId: string;
}) => {
  const [opened, setOpened] = useState(false);
  const query = useReviewTranscript(conversationId, messageId, true, opened);
  const byId = new Map((query.data?.messages ?? []).map((m) => [m.id, m]));

  return (
    <details
      className="mt-3 rounded-lg border border-outline bg-surface"
      onToggle={(e) => e.currentTarget.open && setOpened(true)}
    >
      <summary className="cursor-pointer px-3 py-1.5 text-[13px] text-link underline-offset-2 hover:underline">
        Show the conversation
      </summary>
      <div className="border-t border-outline px-3 py-2.5">
        {query.isLoading && <Skeleton className="h-16 w-full" />}
        {query.isError && (
          <p className="text-xs text-danger">
            {query.error instanceof Error ? query.error.message : 'Failed to load'}{' '}
            <button
              type="button"
              className="font-semibold text-link underline-offset-2 hover:underline"
              onClick={() => void query.refetch()}
            >
              Retry
            </button>
          </p>
        )}
        {query.data && (
          <>
            {query.data.messageId === null && (
              <p className="mb-2 text-xs italic text-ink-subtle">
                Rated turn not isolated — showing the thread.
              </p>
            )}
            <Chat.Transcript
              compact
              messages={query.data.messages}
              highlightMessageId={messageId}
              renderMessageExtra={(m) => retrievalLine(byId.get(m.id))}
            />
            {/* The full thread gets its own page — inline expansion turned
                the queue into soup (user call). */}
            <Link
              to={`/feedback/conversation/${encodeURIComponent(conversationId)}?m=${encodeURIComponent(messageId)}`}
              className="mt-2.5 inline-block text-xs font-semibold text-link underline-offset-2 hover:underline"
            >
              View full conversation →
            </Link>
          </>
        )}
      </div>
    </details>
  );
};
